import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

// `GET /alerts/history*` (NASAM-621). The swagger documents these responses by
// example only, so the parsing here is lenient where the examples are silent
// (e.g. the SENSOR details) -- the response shapes are the contract.

const required = z.string().trim().min(1);

const namedRefDtoSchema = z.object({
  id: required,
  nameEn: z.string(),
  nameAr: z.string(),
});

const locationDtoSchema = z.object({
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});

export const alertHistoryItemDtoSchema = z.object({
  id: required,
  occurredAt: required,
  company: namedRefDtoSchema.nullish(),
  // Null for an alert on a device that isn't fitted to a vehicle.
  asset: namedRefDtoSchema.extend({ code: z.string().nullish() }).nullish(),
  alertType: required,
  alertNameEn: z.string(),
  alertNameAr: z.string(),
  triggeredValueEn: z.string().nullish(),
  triggeredValueAr: z.string().nullish(),
  location: locationDtoSchema.nullish(),
  // The location's street address in the request's language (Google
  // reverse geocoding); blank when unknown.
  locationLabel: z.string().nullish(),
});

export type AlertHistoryItemDto = z.infer<typeof alertHistoryItemDtoSchema>;

export const alertHistoryListResponseDtoSchema = z.object({
  data: z.array(alertHistoryItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

/** A reading or condition value. A state (STATE) can come as true/false --
 *  `{operator: "IS", value: true}` -- read as 1/0 like its readings. */
const numberOrText = z
  .union([z.number(), z.string(), z.boolean()])
  .transform((value) => (typeof value === "boolean" ? (value ? 1 : 0) : value));

export const sensorDetailsDtoSchema = z.object({
  // An object (named in both languages), or just its name.
  sensor: z
    .union([
      z.object({
        id: z.string().nullish(),
        nameEn: z.string().nullish(),
        nameAr: z.string().nullish(),
        name: z.string().nullish(),
        sensorType: z.string().nullish(),
      }),
      z.string(),
    ])
    .nullish(),
  /** TEMPERATURE, HUMIDITY, WEIGHT (total) or STATE (1/0, no unit). */
  quantity: z.string().nullish(),
  triggeredValue: numberOrText.nullish(),
  unit: z.string().nullish(),
  condition: z
    .object({
      operator: z.string(),
      value: numberOrText.nullish(),
      // The upper end, for BETWEEN and OUTSIDE_RANGE.
      valueTo: numberOrText.nullish(),
    })
    .nullish(),
});

export const speedDetailsDtoSchema = z.object({
  recordedSpeedKph: z.number().nullish(),
  speedLimitKph: z.number().nullish(),
});

export const downtimeDetailsDtoSchema = z.object({
  durationMinutes: z.number().nullish(),
});

/** DRIVER_BEHAVIOUR and ROAD_SAFETY share these; ROAD_SAFETY's own extras
 *  (headway, departure, sign value) aren't shown. */
export const cameraAlarmDetailsDtoSchema = z.object({
  alarmType: z.number().nullish(),
  behaviourEn: z.string().nullish(),
  behaviourAr: z.string().nullish(),
  level: z.number().nullish(),
  driver: z
    .object({
      id: required,
      nameEn: z.string().nullish(),
      nameAr: z.string().nullish(),
    })
    .nullish(),
});

/** `details.type` picks which of the schemas above reads the rest; a type
 *  added after this client keeps only its general fields. */
export const alertHistoryDetailsDtoSchema = z.looseObject({ type: z.string() });

export type AlertHistoryDetailsDto = z.infer<
  typeof alertHistoryDetailsDtoSchema
>;

export const alertHistoryDetailResponseDtoSchema = z.object({
  data: alertHistoryItemDtoSchema.extend({
    details: alertHistoryDetailsDtoSchema.nullish(),
  }),
});

export const alertHistoryTypesResponseDtoSchema = z.object({
  data: z.array(required),
});

const alertEvidenceDtoSchema = z.object({
  id: required,
  fileType: required,
  contentType: z.string(),
  url: required,
  expiresAt: required,
  receivedAt: required,
});

/** An alert's evidence comes with its details (`GET /alerts/history/:id`):
 *  only that list is read, so a detail the screen can't parse yet doesn't
 *  hide it. Missing or empty: none. */
export const alertEvidenceResponseDtoSchema = z.object({
  data: z.object({
    evidence: z.array(alertEvidenceDtoSchema).nullish(),
  }),
});
